import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StandardListLayout } from '@/shared/layout/StandardListLayout';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useEquipmentList } from './hooks/useEquipmentList';
import { EquipmentKpiGrid } from './components/EquipmentKpiGrid';
import { EquipmentTable } from './components/EquipmentTable';
import { EquipmentMobileCards } from './components/EquipmentMobileCards';
import { EquipmentToolbar } from './components/EquipmentToolbar';
import type { EquipmentListItem } from './types/equipmentList.types';

export function EquipmentListPage() {
  const navigate = useNavigate();
  const {
    data, isLoading, isDeleting, filters, filterOptions, page, pageSize,
    canEdit, canDelete,
    handleSearch, handleStatusChange, handleFilterChange, deleteItem, updatePage, updatePageSize,
  } = useEquipmentList();

  const [deleteTarget, setDeleteTarget] = useState<EquipmentListItem | null>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteItem(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <StandardListLayout
      title="Thiết bị"
      description="Quản lý thiết bị phục vụ thi công và bảo dưỡng"
      isLoading={isLoading}
      viewMode="table"
      kpiSection={<EquipmentKpiGrid stats={data.stats} isLoading={isLoading} />}
      toolbarSection={
        <EquipmentToolbar
          filters={filters}
          filterOptions={filterOptions}
          onSearchChange={handleSearch}
          onStatusChange={handleStatusChange}
          onFilterChange={handleFilterChange}
        />
      }
      tableSection={
        <EquipmentTable
          items={data.items}
          total={data.total}
          page={page}
          pageSize={pageSize}
          isLoading={isLoading}
          onView={(id) => navigate(`/equipment/detail/${id}`)}
          onEdit={(id) => navigate(`/equipment/edit/${id}`)}
          onDelete={setDeleteTarget}
          canEdit={canEdit}
          canDelete={canDelete}
          onPageChange={updatePage}
          onPageSizeChange={updatePageSize}
        />
      }
      cardSection={
        <EquipmentMobileCards
          items={data.items}
          total={data.total}
          page={page}
          pageSize={pageSize}
          isLoading={isLoading}
          onView={(id) => navigate(`/equipment/detail/${id}`)}
          onEdit={(id) => navigate(`/equipment/edit/${id}`)}
          onDelete={setDeleteTarget}
          canEdit={canEdit}
          canDelete={canDelete}
          onPageChange={updatePage}
          onPageSizeChange={updatePageSize}
        />
      }
    >
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xóa thiết bị"
        message={
          <span>
            Bạn có chắc chắn muốn xóa thiết bị <strong>{deleteTarget?.name}</strong>? Hành động này không thể hoàn tác.
          </span>
        }
        confirmText="Xóa"
        cancelText="Hủy"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </StandardListLayout>
  );
}

export default EquipmentListPage;
